import { z } from 'zod'

export const OfferTypeSchema = z.enum(['club', 'upsell', 'downsell', 'bundle', 'custom'])

const OfferSummaryItemSchema = z.object({
    id: z.string(),
    productId: z.string(),
    variantId: z.string(),
    priceId: z.string(),
    product: z.object({
        name: z.string(),
        description: z.string(),
    }),
    variant: z.object({
        name: z.string(),
        description: z.string(),
        imageUrl: z.string(),
        grams: z.number().nullable(),
    }),
    sku: z.string(),
    unitAmount: z.number(),
    quantity: z.number(),
    amount: z.number(),
    adjustedAmount: z.number(),
    currency: z.string(),
    adjustments: z.array(z.any()),
    recurring: z.boolean(),
    interval: z.string().optional(),
    intervalCount: z.number().optional(),
    totalBillingCycles: z.number().optional(),
    unitAmountAfterFirstCycle: z.number().optional(),
    isExternalSellingPlan: z.boolean().optional(),
    isInternalSellingPlan: z.boolean().optional(),
    deliverySettings: z.record(z.any()).optional(),
    priceSettings: z.record(z.any()).optional(),
    orderLineItemProduct: z.object({
        name: z.string(),
    }),
    orderLineItemVariant: z.object({
        name: z.string(),
        imageUrl: z.string(),
    }),
    metadata: z.record(z.any()),
})

const OfferSummarySchema = z.object({
    items: z.array(OfferSummaryItemSchema),
    totalWeight: z.number(),
    subtotalAmount: z.number(),
    lineItemsPromotionAmount: z.number(),
    subtotalAdjustedAmount: z.number(),
    totalPromotionAmount: z.number(),
    lineItemsTaxAmount: z.number(),
    totalTaxAmount: z.number(),
    shippingCost: z.number(),
    shippingCostIsFree: z.boolean(),
    totalAmount: z.number(),
    totalAdjustedAmount: z.number(),
    adjustments: z.array(z.any()),
    currency: z.string(),
    _consolidated: z.boolean(),
})

export const OfferSchema = z.object({
    id: z.string(),
    storeId: z.string(),
    type: z.string(),
    titleTrans: z.record(z.string(), z.string()),
    expiryDate: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    summaries: z.array(OfferSummarySchema),
})

export const OfferLineItemSchema = z.object({
    id: z.string(),
    offerId: z.string(),
    priceId: z.string(),
    productId: z.string(),
    variantId: z.string(),
    quantity: z.number(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
})

export const OfferPromotionSchema = z.object({
    id: z.string(),
    offerId: z.string(),
    promotionId: z.string(),
    createdAt: z.date().optional(),
})

export const OfferWithLineItemsSchema = OfferSchema.extend({
    offerLineItems: z.array(
        OfferLineItemSchema.extend({
            price: z
                .object({
                    id: z.string(),
                    variantId: z.string(),
                    currencyOptions: z.record(z.any()),
                    variant: z
                        .object({
                            id: z.string(),
                            name: z.string(),
                            imageUrl: z.string().nullable(),
                            product: z
                                .object({
                                    id: z.string(),
                                    name: z.string(),
                                    description: z.string().nullable(),
                                    storeId: z.string(),
                                })
                                .nullable(),
                        })
                        .nullable(),
                })
                .nullable(),
        }),
    ),
})

export const OfferWithPromotionsSchema = OfferSchema.extend({
    promotions: z.array(
        OfferPromotionSchema.extend({
            promotion: z.object({
                id: z.string(),
                name: z.string(),
                code: z.string().nullable(),
                automatic: z.boolean(),
            }),
        }),
    ),
})

export const OfferWithRelationsSchema = OfferSchema.extend({
    offerLineItems: z.array(
        OfferLineItemSchema.extend({
            price: z
                .object({
                    id: z.string(),
                    variantId: z.string(),
                    currencyOptions: z.record(z.any()),
                    variant: z
                        .object({
                            id: z.string(),
                            name: z.string(),
                            imageUrl: z.string().nullable(),
                            product: z
                                .object({
                                    id: z.string(),
                                    name: z.string(),
                                    description: z.string().nullable(),
                                    storeId: z.string(),
                                })
                                .nullable(),
                        })
                        .nullable(),
                })
                .nullable(),
        }),
    ),
    promotions: z.array(
        OfferPromotionSchema.extend({
            promotion: z.object({
                id: z.string(),
                name: z.string(),
                code: z.string().nullable(),
                automatic: z.boolean(),
            }),
        }),
    ),
})

export const OfferListItemSchema = OfferWithLineItemsSchema

export const OfferDetailsSchema = OfferWithRelationsSchema

export type OfferTypeType = z.infer<typeof OfferTypeSchema>
export type OfferType = z.infer<typeof OfferSchema>
export type OfferLineItemType = z.infer<typeof OfferLineItemSchema>
export type OfferPromotionType = z.infer<typeof OfferPromotionSchema>
export type OfferWithLineItemsType = z.infer<typeof OfferWithLineItemsSchema>
export type OfferWithPromotionsType = z.infer<typeof OfferWithPromotionsSchema>
export type OfferWithRelationsType = z.infer<typeof OfferWithRelationsSchema>
export type OfferListItemType = z.infer<typeof OfferListItemSchema>
export type OfferDetailsType = z.infer<typeof OfferDetailsSchema>

export const toOfferDto = (data: any): OfferType => {
    return OfferSchema.parse(data)
}

export const toOfferWithLineItemsDto = (data: any): OfferWithLineItemsType => {
    return OfferWithLineItemsSchema.parse(data)
}

export const toOfferWithPromotionsDto = (data: any): OfferWithPromotionsType => {
    return OfferWithPromotionsSchema.parse(data)
}

export const toOfferWithRelationsDto = (data: any): OfferWithRelationsType => {
    return OfferWithRelationsSchema.parse(data)
}

export const toOfferListItemDto = (data: any): OfferListItemType => {
    return OfferListItemSchema.parse(data)
}

export const toOfferDetailsDto = (data: any): OfferDetailsType => {
    return OfferDetailsSchema.parse(data)
}

